import {
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  articles,
  findArticle,
  articleArea,
  articleHref,
  orderedForArea,
  resolveImage,
  slugify,
} from '../lib/content-loader';
import { findSection, AREAS } from '../lib/sections';
import { MarkdownPre } from './CodeBlock';
import { ApiEndpoints } from './ApiEndpoints';
import { TOC } from './TOC';
import './ArticleView.css';

type Props = {
  // Optional TOC container — when provided, ArticleView mounts the TOC
  // into the right rail. Used so the layout can decide where headings render.
  withTOC?: boolean;
};

export function ArticleView({ withTOC = true }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const article = slug ? findArticle(articles, slug) : undefined;

  useEffect(() => {
    document.title = article
      ? `${article.title} — Schedata docs`
      : 'Schedata docs';
  }, [article]);

  if (!article) {
    return (
      <div className="docs-empty">
        Article <code>{location.pathname}</code> not found.{' '}
        <Link to="/">Back to docs home</Link>.
      </div>
    );
  }

  // Send non-canonical paths to the article's real URL (e.g. an old flat
  // `/rest-api` link bounces to `/api/rest-api`). Keeps existing links working.
  const canonical = articleHref(article);
  if (location.pathname !== canonical) {
    return <Navigate to={canonical} replace />;
  }

  const area = articleArea(article);
  const areaMeta = AREAS.find(a => a.id === area);
  const section = findSection(article.section);
  // Prev/next stay within the current area so paging doesn't jump from the
  // API reference into the general docs.
  const localOrdered = orderedForArea(area);
  const i = localOrdered.findIndex(a => a.slug === article.slug);
  const prev = i > 0 ? localOrdered[i - 1] : null;
  const next = i >= 0 && i < localOrdered.length - 1 ? localOrdered[i + 1] : null;

  return (
    <>
      <div className="ds-breadcrumb">
        <Link to={areaMeta?.home ?? '/'}>{areaMeta?.label ?? 'Docs'}</Link>
        <span className="sep">/</span>
        <span>{section?.title ?? article.section}</span>
        <span className="sep">/</span>
        <span className="current">{article.title}</span>
      </div>

      <article className="docs-article">
        <header className="docs-article-head">
          <div className="docs-eyebrow">
            {section?.title ?? article.section}
          </div>
          <h1>{article.title}</h1>
          {article.summary && (
            <p className="docs-summary">{article.summary}</p>
          )}
          {(article.time || article.updated) && (
            <div className="docs-article-meta">
              {article.time && (
                <span>
                  <i className="fa-regular fa-clock"></i>
                  {article.time}
                </span>
              )}
              {article.time && article.updated && <span>·</span>}
              {article.updated && <span>Updated {article.updated}</span>}
            </div>
          )}
        </header>
        <div className="docs-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            // rehypeRaw lets authors embed HTML in markdown (pills, doc cards,
            // banners, callouts). Content is bundled at build time — never
            // user-input — so the lack of sanitization is by design.
            rehypePlugins={[rehypeRaw]}
            components={{
              h2: ({ children }) => (
                <h2 id={slugify(stringifyChildren(children))}>{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 id={slugify(stringifyChildren(children))}>{children}</h3>
              ),
              pre: MarkdownPre,
              // Generated REST resource pages embed <api-endpoints tag="…">,
              // rendered as a Swagger-style endpoint browser.
              'api-endpoints': ApiEndpoints,
              // Resolve relative `./_images/…` screenshot paths to the
              // Vite-served asset URL (keyed by the article's section).
              img: ({ src, alt }) => {
                const raw = typeof src === 'string' ? src : '';
                const resolved = raw.includes('_images/')
                  ? resolveImage(article.section, raw) ?? raw
                  : raw;
                return <img src={resolved} alt={alt ?? ''} loading="lazy" />;
              },
              // Cast: react-markdown's Components type keys on known HTML tags,
              // so the custom `api-endpoints` element needs the assertion.
            } as Components}
          >
            {article.body}
          </ReactMarkdown>
        </div>
      </article>

      <div className="ds-pager">
        {prev ? (
          <Link className="ds-pager-link" to={articleHref(prev)}>
            <span className="ds-pager-eyebrow">
              <i className="fa-solid fa-arrow-left"></i>Previous
            </span>
            <span className="ds-pager-title">{prev.title}</span>
          </Link>
        ) : (
          <div />
        )}
        {next ? (
          <Link className="ds-pager-link next" to={articleHref(next)}>
            <span className="ds-pager-eyebrow">
              Next<i className="fa-solid fa-arrow-right"></i>
            </span>
            <span className="ds-pager-title">{next.title}</span>
          </Link>
        ) : (
          <div />
        )}
      </div>

      {withTOC && (
        <TOCSlotPortal>
          <TOC body={article.body} />
        </TOCSlotPortal>
      )}
    </>
  );
}

// react-markdown passes children as a heterogeneous React node — for heading
// ids we just need the text content. This walks the tree and concatenates strings.
function stringifyChildren(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(stringifyChildren).join('');
  if (
    node &&
    typeof node === 'object' &&
    'props' in node &&
    node.props &&
    typeof node.props === 'object'
  ) {
    const props = node.props as { children?: React.ReactNode };
    return stringifyChildren(props.children);
  }
  return '';
}

// Mount children into the #ds-toc-slot element rendered by App. This keeps
// the TOC visually in the right rail while letting ArticleView own the data.
function TOCSlotPortal({ children }: { children: ReactNode }) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setEl(document.getElementById('ds-toc-slot'));
  }, []);
  if (!el) return null;
  return createPortal(children, el);
}
